import React, { useEffect, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { useFacility } from '../../context/FacilityContext';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Switch,
  TextField,
} from '@mui/material';
import DashboardLayout from '../../Components/Dashboard/DashboardLayout/DashboardLayout';
import { Add, Save, KeyboardBackspace } from '@mui/icons-material';
import './SignageEditor.css';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebase/config';
import { URL_BASE } from '../../config/config';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import Draggable from './Draggable';
import Droppable from './Droppable';
import SortableItem from './SortableItem';
import { uploadFile } from './uploadFile';
import { uploadFileToS3 } from '../../utils/s3Operations';
import { deleteImagesFunction } from '../../firebase/functions';
import { getNewYorkTimeZoneDate } from '../../utils/dayjsUs';

function SignageEditor() {
  const { facilityId } = useFacility();
  const { claims, currentUser, userFirstName, userLastName } = useAuth();

  const [playlists, setPlaylists] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [assetsList, setAssetsList] = useState([]);
  const [openAddPlaylist, setOpenAddPlaylist] = useState(false);
  const [openAddAsset, setOpenAddAsset] = useState(false);
  const [openEditAsset, setOpenEditAsset] = useState(false);
  const [formname, setFormname] = useState('');
  const [newAsset, setNewAsset] = useState({
    url: '',
    file: null,
  });
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [editAsset, setEditAsset] = useState(null);

  const [isLoading, setIsLoading] = useState(false);

  const [infoAlert, setInfoAlert] = useState('');
  const [errorAlert, setErrorAlert] = useState('');

  const [activeId, setActiveId] = useState(null);
  const [activeDragAsset, setActiveDragAsset] = useState(null);

  const getAssetUrl = (asset) => {
    if (asset && asset.url && typeof asset.url === 'object') {
      return asset.url.url;
    }
    return asset && asset.url;
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
  );

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
    let found = assetsList.find((asset) => asset.name === event.active.id);
    if (!found) {
      found = selectedAssets.find((asset) => asset.name === event.active.id);
    }
    setActiveDragAsset(found || null);
  };

  const handleDragOver = (event) => {
    // no-op for now
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over) return;

    const overId = over.id;
    const activeId = active.id;

    const isActiveInMain = selectedAssets.some((a) => a.name === activeId);
    const isOverInMain = selectedAssets.some((a) => a.name === overId);

    if (isActiveInMain && isOverInMain && activeId !== overId) {
      const oldIndex = selectedAssets.findIndex((a) => a.name === activeId);
      const newIndex = selectedAssets.findIndex((a) => a.name === overId);
      setSelectedAssets((items) => arrayMove(items, oldIndex, newIndex));
      return;
    }

    if (overId === 'editorMainContent') {
      if (!isActiveInMain) {
        const draggedAsset = assetsList.find(
          (asset) => asset.name === activeId,
        );
        if (draggedAsset) {
          setSelectedAssets((prev) => [...prev, draggedAsset]);
        }
      }
      return;
    }

    if (!isActiveInMain && isOverInMain) {
      const draggedAsset = assetsList.find((asset) => asset.name === activeId);
      if (draggedAsset) {
        const overIndex = selectedAssets.findIndex((a) => a.name === overId);
        if (overIndex === -1) {
          setSelectedAssets((prev) => [...prev, draggedAsset]);
        } else {
          const newArray = [...selectedAssets];
          newArray.splice(overIndex, 0, draggedAsset);
          setSelectedAssets(newArray);
        }
      }
    }
  };

  const resetAlerts = () => {
    setInfoAlert('');
    setErrorAlert('');
  };

  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setNewAsset({ url: URL.createObjectURL(file), file });
    }
  };

  const addNewAsset = async () => {
    if (!formname.trim()) {
      setErrorAlert('Asset name is required.');
      return;
    }
    if (!newAsset.file) {
      setErrorAlert('An image, video, or PDF file is required.');
      return;
    }

    setIsLoading(true);
    resetAlerts();

    const playListAssetCollectionRef = collection(db, 'playListAssets');

    try {
      const { url, fileType } = await uploadFile(newAsset.file);

      await addDoc(playListAssetCollectionRef, {
        assetType: fileType,
        facilityId,
        name: formname,
        url,
        duration: 10,
        author: `${userFirstName} ${userLastName}`,
        createdAt: Date.now(),
      });

      setInfoAlert('Asset added successfully!');
      setFormname('');
      setNewAsset({ url: '', file: null });
    } catch (error) {
      console.error('Error adding asset:', error);
      setErrorAlert('Failed to add asset. Please try again.');
    } finally {
      getAssets();
      setIsLoading(false);
      setOpenAddAsset(false);
    }
  };

  const getAssets = async () => {
    try {
      const q = query(
        collection(db, 'playListAssets'),
        where('facilityId', '==', facilityId),
      );
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      setAssetsList([
        {
          assetType: 'link',
          name: 'Activities Link',
          url: `${URL_BASE}digital-signage?facility=${facilityId}&only=activities`,
          duration: 10,
        },
        {
          assetType: 'link',
          name: 'Meals Link',
          url: `${URL_BASE}digital-signage?facility=${facilityId}&only=meals`,
          duration: 10,
        },
        {
          assetType: 'link',
          name: 'Memory Care Link',
          url: `${URL_BASE}digital-signage?facility=${facilityId}&only=memoryCare`,
          duration: 10,
        },
        {
          assetType: 'link',
          name: 'Scheduler',
          url: `${URL_BASE}signage-scheduler?facility=${facilityId}`,
          duration: 10,
        },
        ...data,
      ]);
    } catch (error) {
      console.error('Error fetching assets:', error);
    }
  };

  const deleteAsset = async (asset) => {
    try {
      const assetRef = doc(db, 'playListAssets', asset.id);
      await deleteDoc(assetRef);

      await deleteImagesFunction({
        imageUrls: [getAssetUrl(asset)],
        deleteFromFirestore: false,
      });

      setAssetsList((prevList) =>
        prevList.filter((existingAsset) => existingAsset.id !== asset.id),
      );
    } catch (error) {
      console.error('Error deleting asset:', error);
    } finally {
      getAssets();
      setEditAsset(null);
      setOpenEditAsset(false);
    }
  };

  const handleSaveAsset = async (updatedAsset) => {
    try {
      const assetRef = doc(db, 'playListAssets', updatedAsset.id);
      await updateDoc(assetRef, {
        name: updatedAsset.name,
      });

      setAssetsList((prevList) =>
        prevList.map((asset) =>
          asset.id === updatedAsset.id ? updatedAsset : asset,
        ),
      );
      console.log('Asset updated successfully!');
    } catch (error) {
      console.error('Error updating asset in Firestore:', error);
    } finally {
      getAssets();
      setOpenEditAsset(false);
      setEditAsset(null);
    }
  };

  const addNewPlaylist = async () => {
    if (!formname.trim()) {
      setErrorAlert('Playlist name is required.');
      return;
    }
    if (!newAsset.file) {
      setErrorAlert('An image or video is required.');
      return;
    }

    setIsLoading(true);
    resetAlerts();

    const playListCollectionRef = collection(db, 'playList');

    try {
      const fileUrl = await uploadFileToS3(newAsset.file, newAsset.file.type);

      await addDoc(playListCollectionRef, {
        facilityId,
        playListName: formname,
        playListImageUrl: fileUrl,
        active: false,
        createdBy: currentUser.uid,
        createdAt: Date.now(),
      });

      setInfoAlert('Playlist added successfully!');
      setFormname('');
      setNewAsset({ url: '', file: null });
    } catch (error) {
      console.error('Error adding playlist:', error);
      setErrorAlert('Failed to add playlist. Please try again.');
    } finally {
      fetchPlaylists();
      setIsLoading(false);
      setOpenAddPlaylist(false);
    }
  };

  const fetchPlaylists = async () => {
    setIsLoading(true);
    setErrorAlert('');

    try {
      const playListCollectionRef = collection(db, 'playList');
      const q = query(
        playListCollectionRef,
        where('facilityId', '==', facilityId),
      );
      const querySnapshot = await getDocs(q);

      const fetchedPlaylists = await Promise.all(
        querySnapshot.docs.map(async (docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            ...data,
            playListUrl: `${URL_BASE}playlist?id=${docSnap.id}`,
          };
        }),
      );

      setPlaylists(fetchedPlaylists);
    } catch (error) {
      console.error('Error fetching playlists:', error);
      setErrorAlert('Failed to fetch playlists. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const deletePlaylist = async (playlist) => {
    try {
      const playlistRef = doc(db, 'playList', playlist.id);
      await deleteDoc(playlistRef);

      await deleteImagesFunction({
        imageUrls: [playlist.playListImageUrl],
        deleteFromFirestore: false,
      });

      return { success: true, message: 'Playlist deleted successfully.' };
    } catch (error) {
      console.error('Error deleting playlist:', error);
    } finally {
      fetchPlaylists();
    }
  };

  /**
   * Update playlist's array of assets
   */
  const updateSelectedPlayList = async (playlist, selectedAssets) => {
    setIsLoading(true);
    try {
      const formattedAssets = selectedAssets?.map((asset) => ({
        name: asset.name,
        url: getAssetUrl(asset),
        duration: asset.duration || 10,
        assetType: asset.assetType,
      }));

      const playlistDocRef = doc(db, 'playList', playlist.id);
      await updateDoc(playlistDocRef, {
        assets: formattedAssets || [],
        active: playlist.active,
      });
    } catch (error) {
      console.error('Error updating playlist:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const setSelectedAssetTime = (event, index) => {
    const value = event.target.value;
    setSelectedAssets((prevAssets) => {
      const updatedAssets = [...prevAssets];
      updatedAssets[index] = {
        ...updatedAssets[index],
        duration: value ? parseInt(value, 10) : '',
      };
      return updatedAssets;
    });
  };

  useEffect(() => {
    if (facilityId) {
      fetchPlaylists();
      getAssets();
    }
  }, [facilityId]);

  return (
    <div>
      <DashboardLayout facilityId={facilityId} isLoading={isLoading}>
        {!selectedPlaylist ? (
          <Box className="playlistView">
            <div className="playlistHeader">
              <h2>Playlists</h2>
              <Button
                className="playlistHeaderCTA"
                type="button"
                variant="contained"
                disableElevation
                startIcon={<Add />}
                onClick={() => setOpenAddPlaylist(true)}
              >
                Add New Playlist
              </Button>
            </div>

            <Box className="playlistContainer">
              {playlists.map((playlist) => (
                <div key={playlist.id} className="playlistCard">
                  <img
                    className="playlistImg"
                    src={playlist.playListImageUrl}
                    alt="playlist Image"
                  />
                  <h3>{playlist.playListName}</h3>

                  <div className="playlistActionsContainer">
                    {/* Edit playlist */}
                    <svg
                      className="editSVG"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 512 512"
                      onClick={() => {
                        setSelectedPlaylist(playlist);
                        setSelectedAssets(playlist.assets || []);
                      }}
                    >
                      <path d="M471.6 21.7c-21.9-21.9-57.3-21.9-79.2 0L362.3 51.7l97.9 97.9 30.1-30.1c21.9-21.9 21.9-57.3 0-79.2L471.6 21.7zm-299.2 220c-6.1 6.1-10.8 13.6-13.5 21.9l-29.6 88.8c-2.9 8.6-.6 18.1 5.8 24.6s15.9 8.7 24.6 5.8l88.8-29.6c8.2-2.7 15.7-7.4 21.9-13.5L437.7 172.3 339.7 74.3 172.4 241.7zM96 64C43 64 0 107 0 160L0 416c0 53 43 96 96 96l256 0c53 0 96-43 96-96l0-96c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 96c0 17.7-14.3 32-32 32L96 448c-17.7 0-32-14.3-32-32l0-256c0-17.7 14.3-32 32-32l96 0c17.7 0 32-14.3 32-32s-14.3-32-32-32L96 64z" />
                    </svg>

                    {/* Copy URL */}
                    <svg
                      className="copySVG"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 448 512"
                      onClick={() => {
                        navigator.clipboard.writeText(playlist.playListUrl);
                      }}
                    >
                      <path d="M208 0L332.1 0c12.7 0 24.9 5.1 33.9 14.1l67.9 67.9c9 9 14.1 21.2 14.1 33.9L448 336c0 26.5-21.5 48-48 48l-192 0c-26.5 0-48-21.5-48-48l0-288c0-26.5 21.5-48 48-48zM48 128l80 0 0 64-64 0 0 256 192 0 0-32 64 0 0 48c0 26.5-21.5 48-48 48L48 512c-26.5 0-48-21.5-48-48L0 176c0-26.5 21.5-48 48-48z" />
                    </svg>

                    {playlist.active && claims.superadmin ? (
                      <svg
                        className="deleteSVG"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 448 512"
                        onClick={() => {
                          deletePlaylist(playlist);
                        }}
                      >
                        <path d="M135.2 17.7C140.6 6.8 151.7 0 163.8 0L284.2 0c12.1 0 23.2 6.8 28.6 17.7L320 32l96 0c17.7 0 32 14.3 32 32s-14.3 32-32 32L32 96C14.3 96 0 81.7 0 64S14.3 32 32 32l96 0 7.2-14.3zM32 128l384 0 0 320c0 35.3-28.7 64-64 64L96 512c-35.3 0-64-28.7-64-64l0-320zm96 64c-8.8 0-16 7.2-16 16l0 224c0 8.8 7.2 16 16 16s16-7.2 16-16l0-224c0-8.8-7.2-16-16-16zm96 0c-8.8 0-16 7.2-16 16l0 224c0 8.8 7.2 16 16 16s16-7.2 16-16l0-224c0-8.8-7.2-16-16-16zm96 0c-8.8 0-16 7.2-16 16l0 224c0 8.8 7.2 16 16 16s16-7.2 16-16l0-224c0-8.8-7.2-16-16-16z" />
                      </svg>
                    ) : (
                      !playlist.active && (
                        <svg
                          className="deleteSVG"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 448 512"
                          onClick={() => {
                            deletePlaylist(playlist);
                          }}
                        >
                          <path d="M135.2 17.7C140.6 6.8 151.7 0 163.8 0L284.2 0c12.1 0 23.2 6.8 28.6 17.7L320 32l96 0c17.7 0 32 14.3 32 32s-14.3 32-32 32L32 96C14.3 96 0 81.7 0 64S14.3 32 32 32l96 0 7.2-14.3zM32 128l384 0 0 320c0 35.3-28.7 64-64 64L96 512c-35.3 0-64-28.7-64-64l0-320zm96 64c-8.8 0-16 7.2-16 16l0 224c0 8.8 7.2 16 16 16s16-7.2 16-16l0-224c0-8.8-7.2-16-16-16zm96 0c-8.8 0-16 7.2-16 16l0 224c0 8.8 7.2 16 16 16s16-7.2 16-16l0-224c0-8.8-7.2-16-16-16zm96 0c-8.8 0-16 7.2-16 16l0 224c0 8.8 7.2 16 16 16s16-7.2 16-16l0-224c0-8.8-7.2-16-16-16z" />
                        </svg>
                      )
                    )}
                  </div>
                </div>
              ))}
            </Box>
          </Box>
        ) : (
          // Editing a specific playlist:
          <Box className="signageEditorView">
            <div className="playlistHeader">
              <Button
                className="playlistHeaderCTA"
                type="button"
                variant="contained"
                disableElevation
                startIcon={<KeyboardBackspace />}
                onClick={() => setSelectedPlaylist(null)}
              >
                Back to All Playlists
              </Button>
            </div>

            <div className="editorContainer">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
              >
                <Droppable id="editorMainContent">
                  <div className="editorMainHeader">
                    <h3 style={{ marginBottom: 10 }}>
                      {selectedPlaylist.playListName}
                    </h3>
                    <div className="playlistDuration">
                      <p className="durationSeconds">
                        {selectedAssets &&
                          selectedAssets
                            .map((asset) => {
                              const duration = parseInt(asset.duration);
                              return isNaN(duration) ? 0 : duration;
                            })
                            .reduce((a, b) => a + b, 0)}
                      </p>
                      <p>seconds</p>
                    </div>

                    {claims?.superadmin && (
                      <div className="activeToggle">
                        <p>Active?</p>
                        <Switch
                          checked={selectedPlaylist.active}
                          onChange={(event) => {
                            setSelectedPlaylist((prevPlaylist) => ({
                              ...prevPlaylist,
                              active: !prevPlaylist.active,
                            }));
                          }}
                        />
                      </div>
                    )}

                    <a
                      target="_blank"
                      rel="noreferrer"
                      href={selectedPlaylist.playListUrl}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                      >
                        <path d="M320 0c-17.7 0-32 14.3-32 32s14.3 32 32 32l82.7 0L201.4 265.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L448 109.3l0 82.7c0 17.7 14.3 32 32 32s32-14.3 32-32l0-160c0-17.7-14.3-32-32-32L320 0zM80 32C35.8 32 0 67.8 0 112L0 432c0 44.2 35.8 80 80 80l320 0c44.2 0 80-35.8 80-80l0-112c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 112c0 8.8-7.2 16-16 16L80 448c-8.8 0-16-7.2-16-16l0-320c0-8.8 7.2-16 16-16l96 0c17.7 0 32-14.3 32-32s-14.3-32-32-32L80 32z" />
                      </svg>
                    </a>
                  </div>

                  <SortableContext
                    items={selectedAssets.map((asset) => asset.name)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="editorMainContent">
                      {selectedAssets?.length > 0 &&
                        selectedAssets.map((asset, index) => (
                          <SortableItem
                            key={asset.name}
                            asset={asset}
                            index={index}
                            setSelectedAssetTime={setSelectedAssetTime}
                            setSelectedAssets={setSelectedAssets}
                          />
                        ))}
                      <div className="editorCTAButton">
                        <Button
                          className="editorCTA"
                          type="button"
                          variant="contained"
                          disableElevation
                          startIcon={<Save />}
                          onClick={() =>
                            updateSelectedPlayList(
                              selectedPlaylist,
                              selectedAssets,
                            )
                          }
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                  </SortableContext>
                </Droppable>

                <div className="editorSidebar">
                  <h4>Assets</h4>
                  <p>Drag and drop to the left</p>

                  {assetsList.map((asset, index) => (
                    <div key={index} className="sidebarAssetContainer">
                      <Draggable id={asset.name}>
                        {asset.assetType === 'link' ? (
                          <>
                            <div className="assetIcon">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 640 512"
                              >
                                <path d="M579.8 267.7c56.5-56.5 56.5-148 0-204.5c-50-50-128.8-56.5-186.3-15.4l-1.6 1.1c-14.4 10.3-17.7 30.3-7.4 44.6s30.3 17.7 44.6 7.4l1.6-1.1c32.1-22.9 76-19.3 103.8 8.6c31.5 31.5 31.5 82.5 0 114L422.3 334.8c-31.5 31.5-82.5 31.5-114 0c-27.9-27.9-31.5-71.8-8.6-103.8l1.1-1.6c10.3-14.4 6.9-34.4-7.4-44.6s-34.4-6.9-44.6 7.4l-1.1 1.6C206.5 251.2 213 330 263 380c56.5 56.5 148 56.5 204.5 0L579.8 267.7zM60.2 244.3c-56.5 56.5-56.5 148 0 204.5c50 50 128.8 56.5 186.3 15.4l1.6-1.1c14.4-10.3 17.7-30.3 7.4-44.6s-30.3-17.7-44.6-7.4l-1.6 1.1c-32.1 22.9-76 19.3-103.8-8.6C74 372 74 321 105.5 289.5L217.7 177.2c31.5-31.5 82.5-31.5 114 0c27.9 27.9 31.5 71.8 8.6 103.9l-1.1 1.6c-10.3 14.4-6.9 34.4 7.4 44.6s34.4 6.9 44.6-7.4l1.1-1.6C433.5 260.8 427 182 377 132c-56.5-56.5-148-56.5-204.5 0L60.2 244.3z" />
                              </svg>
                            </div>
                            <p>{asset.name}</p>
                          </>
                        ) : asset.assetType === 'image' ? (
                          <>
                            <div className="assetIcon">
                              <img src={getAssetUrl(asset)} alt={asset.name} />
                            </div>
                            <p>{asset.name}</p>
                          </>
                        ) : (
                          <>
                            <div className="assetIcon">
                              <svg
                                fill="#000000"
                                height="800px"
                                width="800px"
                                version="1.1"
                                viewBox="0 0 491.521 491.521"
                              >
                                <g>
                                  <path
                                    d="M491.41,245.759c-1.353-57.804-15.123-115.305-39.652-139.832c-72.832-72.831-339.164-72.831-411.997,0
			C15.234,130.454,1.463,187.955,0.11,245.759c-1.433,61.235,11.07,122.81,39.651,145.906c80.113,64.736,331.884,64.736,411.997,0
			C480.338,368.569,492.844,306.994,491.41,245.759z M320.991,259.378l-130.453,95.838c-11.161,8.199-26.904,0.23-26.904-13.617
			v-95.84v-95.836c0-13.85,15.743-21.819,26.904-13.619l130.453,95.836c4.596,3.378,6.894,8.496,6.894,13.619
			C327.885,250.88,325.587,256.002,320.991,259.378z"
                                  />
                                </g>
                              </svg>
                            </div>
                            <p>{asset.name}</p>
                          </>
                        )}
                      </Draggable>

                      <div
                        className="editButton"
                        onClick={(event) => {
                          event.stopPropagation();
                          setOpenEditAsset(true);
                          setEditAsset(asset);
                        }}
                      >
                        <svg
                          className="editSVG"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 512 512"
                        >
                          <path d="M471.6 21.7c-21.9-21.9-57.3-21.9-79.2 0L362.3 51.7l97.9 97.9 30.1-30.1c21.9-21.9 21.9-57.3 0-79.2L471.6 21.7zm-299.2 220c-6.1 6.1-10.8 13.6-13.5 21.9l-29.6 88.8c-2.9 8.6-.6 18.1 5.8 24.6s15.9 8.7 24.6 5.8l88.8-29.6c8.2-2.7 15.7-7.4 21.9-13.5L437.7 172.3 339.7 74.3 172.4 241.7zM96 64C43 64 0 107 0 160L0 416c0 53 43 96 96 96l256 0c53 0 96-43 96-96l0-96c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 96c0 17.7-14.3 32-32 32L96 448c-17.7 0-32-14.3-32-32l0-256c0-17.7 14.3-32 32-32l96 0c17.7 0 32-14.3 32-32s-14.3-32-32-32L96 64z" />
                        </svg>
                      </div>
                    </div>
                  ))}

                  <DragOverlay>
                    {activeDragAsset ? (
                      <div className="draggedItem">{activeDragAsset.name}</div>
                    ) : null}
                  </DragOverlay>
                  <div className="editorCTAButton">
                    <Button
                      className="editorCTA"
                      type="button"
                      variant="contained"
                      disableElevation
                      startIcon={<Add />}
                      onClick={() => setOpenAddAsset(true)}
                    >
                      Upload Asset
                    </Button>
                  </div>
                </div>
              </DndContext>
            </div>
          </Box>
        )}

        <Dialog
          open={openAddPlaylist}
          onClose={() => setOpenAddPlaylist(false)}
        >
          <DialogTitle>Add New Playlist</DialogTitle>
          <DialogContent>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                paddingTop: 2,
              }}
            >
              <TextField
                id="outlined-basic"
                label="Playlist Name"
                variant="outlined"
                value={formname}
                onChange={(e) => setFormname(e.target.value)}
              />
              {newAsset.url && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    gap: 2,
                    mt: 2,
                  }}
                >
                  <img
                    src={newAsset.url}
                    alt={`Selected Image ${newAsset.url}`}
                    style={{
                      maxWidth: '100px',
                      maxHeight: '100px',
                      borderRadius: '8px',
                      objectFit: 'cover',
                    }}
                  />
                </Box>
              )}
              <Button variant="contained" component="label">
                Select Images
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  multiple
                  onChange={handleImageSelect}
                />
              </Button>
            </Box>
            <hr />
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', mb: 2 }}>
            <Button
              onClick={() => {
                setOpenAddPlaylist(false);
                setNewAsset({});
                setFormname('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={addNewPlaylist}
              variant="contained"
              color="primary"
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openAddAsset} onClose={() => setOpenAddAsset(false)}>
          <DialogTitle>Add New Asset</DialogTitle>
          <DialogContent>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                paddingTop: 2,
              }}
            >
              <TextField
                id="outlined-basic"
                label="Asset Name"
                variant="outlined"
                value={formname}
                onChange={(e) => setFormname(e.target.value)}
              />
              {newAsset.url && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    gap: 2,
                    mt: 2,
                  }}
                >
                  {newAsset.file && newAsset.file.type.startsWith('video/') ? (
                    <video
                      src={newAsset.url}
                      controls
                      style={{
                        maxWidth: '300px',
                        maxHeight: '100px',
                        borderRadius: '8px',
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    <img
                      src={newAsset.url}
                      alt={`Selected Asset Preview`}
                      style={{
                        maxWidth: '100px',
                        maxHeight: '100px',
                        borderRadius: '8px',
                        objectFit: 'cover',
                      }}
                    />
                  )}
                </Box>
              )}
              <Button variant="contained" component="label">
                Select Asset
                <input
                  type="file"
                  hidden
                  accept="image/*,video/*,application/pdf"
                  onChange={handleImageSelect}
                />
              </Button>
            </Box>
            <hr />
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', mb: 2 }}>
            <Button
              onClick={() => {
                setOpenAddAsset(false);
                setNewAsset({});
                setFormname('');
              }}
            >
              Cancel
            </Button>
            <Button onClick={addNewAsset} variant="contained" color="primary">
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={openEditAsset}
          onClose={() => {
            setOpenEditAsset(false);
            setEditAsset(null);
          }}
        >
          <DialogContent>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                paddingTop: 2,
              }}
            >
              <div className="assetPopup">
                <div className="overlay"></div>
                <div className="assetPopupContainer">
                  <div className="assetPopupHeader">
                    <h4>Edit Asset</h4>
                    <div
                      className="XButton"
                      onClick={() => {
                        setOpenEditAsset(false);
                        setEditAsset(null);
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 384 512"
                      >
                        <path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z" />
                      </svg>
                    </div>
                  </div>
                  <div className="assetPopupContent">
                    <div className="fileName">
                      <p>Name:</p>
                      <input
                        className="editAssetName"
                        type="text"
                        value={editAsset?.name || ''}
                        onChange={(e) =>
                          setEditAsset({ ...editAsset, name: e.target.value })
                        }
                      />
                    </div>
                    <div className="CreatedOn">
                      <p>Created:</p>
                      <p className="popupFixedText">
                        {editAsset?.createdAt
                          ? getNewYorkTimeZoneDate(editAsset.createdAt).format(
                              'MM/DD/YYYY',
                            )
                          : 'N/A'}
                      </p>
                    </div>
                    <div className="author">
                      <p>Author:</p>
                      <p className="popupFixedText">
                        {editAsset?.assetType === 'link'
                          ? 'Centara'
                          : editAsset?.author}
                      </p>
                    </div>
                    {editAsset?.assetType !== 'link' && (
                      <div className="popupActions">
                        <Button
                          className="popupSave"
                          type="button"
                          variant="contained"
                          disableElevation
                          onClick={() => handleSaveAsset(editAsset)}
                        >
                          Save
                        </Button>

                        <Button
                          className="popupDelete"
                          type="button"
                          variant="contained"
                          disableElevation
                          onClick={() => deleteAsset(editAsset)}
                        >
                          Delete Asset
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="assetPopupImage">
                    {editAsset?.assetType === 'link' ? (
                      <iframe
                        src={getAssetUrl(editAsset)}
                        style={{
                          width: '100%',
                          height: '100%',
                          border: 'none',
                          display: 'block',
                          borderRadius: '0.4rem',
                        }}
                        title="Asset Link Preview"
                      />
                    ) : editAsset?.assetType === 'image' ? (
                      <img src={getAssetUrl(editAsset)} alt={editAsset?.name} />
                    ) : editAsset?.assetType === 'video' ? (
                      <video
                        height="100%"
                        width="100%"
                        src={getAssetUrl(editAsset)}
                        controls
                      />
                    ) : null}
                  </div>
                </div>
              </div>
            </Box>
            <hr />
          </DialogContent>
        </Dialog>
      </DashboardLayout>
    </div>
  );
}

export default SignageEditor;
